import {
  Box,
  Collapse,
  Grid,
  GridItem,
  Text,
  VStack,
  HStack,
  Checkbox,
  Select,
  NumberInput,
  NumberInputField,
  Input,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import { parseDateString, formatDateToString } from 'utils/helpers';
import { useAnswerCodes } from '../../hooks';
import { ValidatedField } from '..';
import type { CoverageSectionProps } from './types';

const SolarPanelSection = ({
  coverages,
  updateCoverage,
  isLoadingCodes,
}: CoverageSectionProps) => {
  const { getOptions } = useAnswerCodes();

  return (
    <Box>
      <HStack mb={3}>
        <Text fontWeight="700">Solar Panel</Text>
        <Checkbox
          isChecked={coverages.solar_panel_enabled}
          onChange={(e) =>
            updateCoverage('solar_panel_enabled', e.target.checked)
          }
          colorScheme="blue"
        />
      </HStack>
      <Collapse in={coverages.solar_panel_enabled} animateOpacity>
        <Grid templateColumns="1fr 1fr" gap={4}>
          <GridItem>
            <VStack align="stretch" spacing={3}>
              <DateField
                label="Date of Installation"
                date={parseDateString(
                  coverages.solar_panel_date_of_installation,
                )}
                onChange={(date: Date | null) =>
                  updateCoverage(
                    'solar_panel_date_of_installation',
                    formatDateToString(date),
                  )
                }
              />
              <ValidatedField
                itemPath="location"
                fieldPath="coverages.solar_panel_installation_location"
                label="Installation Location"
                labelSize="xs"
              >
                <Input
                  variant="main"
                  size="sm"
                  height={30}
                  value={coverages.solar_panel_installation_location || ''}
                  onChange={(e) =>
                    updateCoverage(
                      'solar_panel_installation_location',
                      e.target.value,
                    )
                  }
                />
              </ValidatedField>
              <ValidatedField
                itemPath="location"
                fieldPath="coverages.solar_panel_capacity"
                label="Capacity"
                labelSize="xs"
              >
                <NumberInput
                  variant="main"
                  size="sm"
                  min={0}
                  value={coverages.solar_panel_capacity ?? ''}
                  onChange={(valueString) =>
                    updateCoverage(
                      'solar_panel_capacity',
                      valueString ? Number(valueString) : null,
                    )
                  }
                >
                  <NumberInputField height={30} placeholder="kWh" />
                </NumberInput>
              </ValidatedField>
              <Checkbox
                isChecked={coverages.solar_panel_tracking}
                onChange={(e) =>
                  updateCoverage('solar_panel_tracking', e.target.checked)
                }
                colorScheme="blue"
              >
                <Text fontSize="sm">Tracking</Text>
              </Checkbox>
              <Checkbox
                isChecked={coverages.solar_panel_leased}
                onChange={(e) =>
                  updateCoverage('solar_panel_leased', e.target.checked)
                }
                colorScheme="blue"
              >
                <Text fontSize="sm">Leased</Text>
              </Checkbox>
              <Checkbox
                isChecked={coverages.solar_panel_tied_to_grid}
                onChange={(e) =>
                  updateCoverage('solar_panel_tied_to_grid', e.target.checked)
                }
                colorScheme="blue"
              >
                <Text fontSize="sm">Equipment Tied to Grid</Text>
              </Checkbox>
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="stretch" spacing={3}>
              <ValidatedField
                itemPath="location"
                fieldPath="coverages.solar_panel_type"
                label="Coverage Type"
                labelSize="xs"
              >
                <Select
                  variant="main"
                  size="sm"
                  height={30}
                  value={coverages.solar_panel_type || ''}
                  onChange={(e) =>
                    updateCoverage('solar_panel_type', e.target.value)
                  }
                  isDisabled={isLoadingCodes}
                >
                  {getOptions('solar_panel_coverage_type').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
              <HStack spacing={3}>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.solar_panel_limit_cad"
                  label="Limit"
                  labelSize="xs"
                >
                  <NumberInput
                    variant="main"
                    size="sm"
                    min={0}
                    value={coverages.solar_panel_limit_cad ?? ''}
                    onChange={(valueString) =>
                      updateCoverage(
                        'solar_panel_limit_cad',
                        valueString ? Number(valueString) : null,
                      )
                    }
                  >
                    <NumberInputField height={30} placeholder="BASE" />
                  </NumberInput>
                </ValidatedField>
                <ValidatedField
                  itemPath="location"
                  fieldPath="coverages.solar_panel_deductible_cad"
                  label="Deductible"
                  labelSize="xs"
                >
                  <NumberInput
                    variant="main"
                    size="sm"
                    min={0}
                    value={coverages.solar_panel_deductible_cad ?? ''}
                    onChange={(valueString) =>
                      updateCoverage(
                        'solar_panel_deductible_cad',
                        valueString ? Number(valueString) : null,
                      )
                    }
                  >
                    <NumberInputField height={30} placeholder="BASE" />
                  </NumberInput>
                </ValidatedField>
              </HStack>
              <ValidatedField
                itemPath="location"
                fieldPath="coverages.solar_panel_settlement_basis"
                label="Settlement Basis"
                labelSize="xs"
              >
                <Select
                  variant="main"
                  size="sm"
                  height={30}
                  value={coverages.solar_panel_settlement_basis || ''}
                  onChange={(e) =>
                    updateCoverage(
                      'solar_panel_settlement_basis',
                      e.target.value,
                    )
                  }
                  isDisabled={isLoadingCodes}
                >
                  {getOptions('solar_panel_settlement_basis').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            </VStack>
          </GridItem>
        </Grid>
      </Collapse>
    </Box>
  );
};

export default SolarPanelSection;
